import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Phone, Mail, Sparkles, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { getSlangOfTheDay } from '../services/api';

// Helper function to get translated slang meaning
const getSlangMeaning = (slang, lang) => {
  if (!slang) return '';
  if (lang === 'es' && slang.meaningEs) return slang.meaningEs;
  if (lang === 'fr' && slang.meaningFr) return slang.meaningFr;
  return slang.meaning;
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [todaySlang, setTodaySlang] = useState(null);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const { language, changeLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get slang word of the day
  const loadSlang = useCallback(async () => {
    try {
      const word = await getSlangOfTheDay();
      setTodaySlang(word);
    } catch (err) {
      // Silently fail - slang display is non-critical
    }
  }, []);

  useEffect(() => {
    loadSlang();
  }, [loadSlang]);

  const navLinks = [
  { name: t('nav.home'), href: '#home' },
  { name: t('nav.services'), href: '#services' },
  { name: t('nav.packages'), href: '#packages' },
  { name: t('nav.revenueCalculator'), href: '#revenue-calculator' },
  { name: t('nav.currencyConverter'), href: '#currency' },
  { name: t('nav.contact'), href: '#contact' }];


  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }];


  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top bar with contact info and slang */}
      <div className={`hidden md:block transition-all duration-300 ${
        isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-auto opacity-100'}`
      }>
        <div className="bg-[#000080] text-white py-2">
          <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <a href={`https://wa.me/18292852485`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#FFFF00] transition-colors">
                <Phone className="w-4 h-4" />
                {companyInfo.phone}
              </a>
              <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-[#FFFF00] transition-colors">
                <Mail className="w-4 h-4" />
                {companyInfo.email}
              </a>
            </div>
            {/* Dominican Slang in Header */}
            {todaySlang &&
            <button 
              onClick={() => scrollToSection('#slang')}
              className="flex items-center gap-2 bg-[#FFFF00] text-[#000080] px-4 py-1 rounded-full hover:bg-[#FFD700] transition-colors cursor-pointer"
            >
                <Sparkles className="w-4 h-4" />
                <span className="font-bold">{t('slangOfDay')}:</span>
                <span className="font-medium">"{todaySlang.word}"</span>
                <span className="text-[#000080]/70">= {getSlangMeaning(todaySlang, language)}</span>
              </button>
            }
            <p className="text-[#FFFF00]">{companyInfo.location}</p>
          </div>
        </div>
      </div>

      {/* Main navigation - JotForm style */}
      <nav className={`transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-[#E0F4FF]'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo - JotForm Style with Image */}
            <a href="#home" className="flex items-center gap-3" onClick={(e) => {e.preventDefault();scrollToSection('#home');}}>
              <img 
                src="https://customer-assets.emergentagent.com/job_c46fcb94-cd59-4f0e-863c-2f57b5de0e04/artifacts/j3ya7gn7_POM%20logo%20no%20background.png"
                alt="Peace of Mind Property Management"
                className="h-16 md:h-20 w-auto"
              />
            </a>

            {/* Desktop navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) =>
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className={`font-medium transition-colors hover:text-[#FFFF00] ${
                isScrolled ? 'text-[#000080]' : 'text-[#000080]'}`
                }>
                  {link.name}
                </button>
              )}
            </div>

            {/* Language Switcher & CTA */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isScrolled ?
                  'text-[#000080] hover:bg-gray-100' :
                  'text-[#000080] hover:bg-white/30'}`
                  }>
                  <Globe className="w-5 h-5" />
                  <span className="font-medium">{languages.find((l) => l.code === language)?.flag}</span>
                </button>
                
                {showLangMenu &&
                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[150px] z-50">
                    {languages.map((lang) =>
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setShowLangMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FFFF00]/20 transition-colors ${
                    language === lang.code ? 'bg-[#FFFF00]/10 font-semibold' : ''}`
                    }>
                        <span className="text-xl">{lang.flag}</span>
                        <span className="text-[#000080]">{lang.name}</span>
                      </button>
                  )}
                  </div>
                }
              </div>

              <Button
                onClick={() => scrollToSection('#contact')}
                className="btn-tropical bg-[#FFFF00] hover:bg-[#FFD700] text-[#000080] font-bold px-6 shadow-lg">
                {t('nav.getStarted')}
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden items-center gap-2">
              {/* Mobile Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  className={`p-2 rounded-lg transition-colors ${
                  isScrolled ? 'text-[#000080] hover:bg-gray-100' : 'text-[#000080] hover:bg-white/30'}`
                  }>
                  <Globe className="w-5 h-5" />
                </button>
                
                {showLangMenu &&
                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[150px] z-50">
                    {languages.map((lang) =>
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setShowLangMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FFFF00]/20 transition-colors ${
                    language === lang.code ? 'bg-[#FFFF00]/10 font-semibold' : ''}`
                    }>
                        <span className="text-xl">{lang.flag}</span>
                        <span className="text-[#000080]">{lang.name}</span>
                      </button>
                  )}
                  </div>
                }
              </div>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-[#000080] hover:bg-gray-100' : 'text-[#000080] hover:bg-white/30'}`
                }>
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen &&
          <div className="lg:hidden mt-4 pb-4 border-t border-[#000080]/20">
              <div className="flex flex-col gap-2 pt-4">
                {navLinks.map((link) =>
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className={`text-left py-3 px-4 rounded-lg font-medium transition-colors ${
                isScrolled ?
                'text-[#000080] hover:bg-[#000080]/5' :
                'text-[#000080] hover:bg-white/30'}`
                }>
                    {link.name}
                  </button>
              )}
                <Button
                onClick={() => scrollToSection('#contact')}
                className="mt-2 btn-tropical bg-[#FFFF00] hover:bg-[#FFD700] text-[#000080] font-bold">
                  {t('nav.getStarted')}
                </Button>
              </div>
            </div>
          }
        </div>
      </nav>
    </header>);

};

export default Header;